import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Lock, Loader2, ArrowRight, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const id = language === "id";
  const token = searchParams.get("token") || "";

  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setVerifyError(id ? "Tautan tidak valid" : "Invalid link");
      return;
    }
    axios.get(`${API}/auth/reset-password/verify`, { params: { token } })
      .then(r => {
        if (r.data?.valid) {
          setVerified(true);
          setEmail(r.data.email || "");
        } else {
          const reason = r.data?.reason || "invalid";
          setVerifyError(
            reason === "expired" ? (id ? "Tautan kedaluwarsa (lebih dari 1 jam)." : "Link expired (over 1 hour old).") :
            reason === "invalid_or_used" ? (id ? "Tautan sudah dipakai atau tidak valid." : "Link already used or invalid.") :
            (id ? "Tautan tidak dapat divalidasi." : "Link could not be validated.")
          );
        }
      })
      .catch(() => setVerifyError(id ? "Tidak dapat memverifikasi tautan." : "Could not verify link."))
      .finally(() => setVerifying(false));
  }, [token, id]);

  const handleSubmit = async () => {
    if (password.length < 6) {
      toast.error(id ? "Kata sandi minimal 6 karakter" : "Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error(id ? "Konfirmasi kata sandi tidak cocok" : "Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success(id ? "Kata sandi diperbarui" : "Password updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || (id ? "Gagal mengatur ulang" : "Reset failed"));
    }
    setLoading(false);
  };

  return (
    <div className="dark-immersive min-h-screen flex items-center justify-center px-6 py-16" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="glass-dark rounded-3xl p-8 sm:p-10"
        >
          {verifying && (
            <div className="text-center py-10" data-testid="reset-verifying">
              <Loader2 size={24} className="animate-spin text-seikat-pink mx-auto mb-4" />
              <p className="font-body text-seikat-bone-muted text-sm">{id ? "Memverifikasi tautan..." : "Verifying link..."}</p>
            </div>
          )}

          {!verifying && verifyError && (
            <div className="text-center" data-testid="reset-invalid">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertCircle size={28} className="text-red-300" strokeWidth={1.4} />
              </div>
              <h1 className="font-heading text-3xl text-seikat-bone">{id ? "Tautan tidak valid" : "Invalid link"}</h1>
              <p className="mt-4 font-body text-seikat-bone/60 text-sm">{verifyError}</p>
              <button onClick={() => navigate("/forgot-password")} className="btn-pill btn-pill--solid mt-8 w-full justify-center" data-testid="reset-request-new">
                {id ? "Minta Tautan Baru" : "Request New Link"} <ArrowRight size={14} />
              </button>
            </div>
          )}

          {!verifying && verified && !done && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-px bg-seikat-pink" />
                <span className="eyebrow-immersive">{id ? "Atur Ulang" : "Reset"}</span>
              </div>
              <h1 className="font-heading text-4xl text-seikat-bone leading-[0.95]">
                {id ? <>Buat kata sandi <em>baru</em></> : <>Create a <em>new</em> password</>}
              </h1>
              <p className="mt-3 font-body text-seikat-bone/60 text-sm">
                {id ? `untuk ${email}` : `for ${email}`}
              </p>
              <div className="mt-8 space-y-4">
                <Field label={id ? "Kata Sandi Baru" : "New Password"}>
                  <div className="relative">
                    <input
                      type={reveal ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="immersive-input pr-10"
                      data-testid="reset-password-input"
                      autoFocus
                    />
                    <button onClick={() => setReveal(v => !v)} type="button" className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-white/5 flex items-center justify-center text-seikat-bone-muted hover:text-seikat-bone transition-colors" aria-label="Toggle visibility">
                      {reveal ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                  </div>
                </Field>
                <Field label={id ? "Konfirmasi Kata Sandi" : "Confirm Password"}>
                  <input
                    type={reveal ? "text" : "password"}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSubmit()}
                    placeholder="••••••••"
                    className="immersive-input"
                    data-testid="reset-confirm-input"
                  />
                </Field>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !password || !confirm}
                  className="btn-pill btn-pill--solid w-full justify-center mt-3 disabled:opacity-50"
                  data-testid="reset-submit-btn"
                  data-magnet
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                  {id ? "Simpan Kata Sandi Baru" : "Save New Password"}
                </button>
              </div>
            </>
          )}

          {done && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="text-center"
              data-testid="reset-done"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-seikat-lime/15 flex items-center justify-center">
                <CheckCircle2 size={28} className="text-seikat-lime" strokeWidth={1.4} />
              </div>
              <p className="eyebrow-immersive text-seikat-lime">{id ? "Berhasil" : "Success"}</p>
              <h1 className="mt-3 font-heading text-3xl text-seikat-bone">
                {id ? <>Kata sandi <em>diperbarui</em></> : <>Password <em>updated</em></>}
              </h1>
              <p className="mt-4 font-body text-seikat-bone/60 text-sm">
                {id ? "Sekarang kamu bisa masuk dengan kata sandi baru." : "You can now sign in with your new password."}
              </p>
              <button onClick={() => navigate("/login")} className="btn-pill btn-pill--solid mt-8 w-full justify-center" data-testid="reset-go-login">
                {id ? "Masuk Sekarang" : "Sign In Now"} <ArrowRight size={14} />
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 font-body text-[10px] tracking-[0.25em] uppercase text-seikat-bone-muted">{label}</div>
      {children}
    </label>
  );
}
